import { AnimatePresence, motion } from "framer-motion";
import { CheckCircle, X } from "lucide-react";
import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Plan, plans } from "../../data/plans";
import dreamWorldProvider from "../../DreamWorldProvider";
import { selectUserGlobalData } from "../../store/dreamworldSlice";
import { CreatePayment } from "../../utils/PiIntegration";

interface SubscribeModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const SubscribeModal: React.FC<SubscribeModalProps> = ({ isOpen, onClose }) => {
  const [billingCycle, setBillingCycle] = useState<"monthly" | "yearly">(
    "monthly"
  );
  const userGlobalData = useSelector(selectUserGlobalData);
  const dispatch = useDispatch();
  const handleSubscribe = async (plan: Plan) => {
    if (!userGlobalData) {
      alert("You must be logged in to subscribe.");
      return;
    }

    try {
      await CreatePayment(
        userGlobalData?.userData?.uid,
        billingCycle === "monthly" ? plan.price.monthly : plan.price.yearly,
        "proSubscription",
        async () => {
          await dreamWorldProvider.subscribeToPlan(
            userGlobalData.user.uid,
            plan.id,
            billingCycle
          );

          onClose();
          setTimeout(() => {
            alert("Subscription successful!");
          }, 200);
        }
      );
    } catch (error) {
      console.error("Error subscribing to plan:", error);
      alert("Failed to subscribe. Please try again.");
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-2 sm:p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            className="bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900 rounded-xl sm:rounded-2xl w-full max-w-sm sm:max-w-4xl border-2 border-purple-500/50 shadow-2xl shadow-purple-500/20 h-fit max-h-[95vh]"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex justify-between items-center p-4 sm:p-6 pb-2 sm:pb-4">
              <div>
                <h2 className="text-xl sm:text-3xl font-bold text-white">
                  Dreamworld Pro
                </h2>
                <p className="text-xs sm:text-sm text-purple-300 mt-1">
                  Unlock your creative potential
                </p>
              </div>
              <button
                onClick={onClose}
                className="p-1.5 sm:p-2 text-gray-400 hover:text-white hover:bg-gray-700/50 rounded-full transition-all duration-200"
              >
                <X className="w-4 h-4 sm:w-6 sm:h-6" />
              </button>
            </div>

            {/* Billing Toggle */}
            <div className="flex justify-center items-center space-x-3 mb-4 sm:mb-6 px-4">
              <span
                className={`cursor-pointer text-sm font-medium transition-colors duration-200 ${
                  billingCycle === "monthly" ? "text-white" : "text-gray-400"
                }`}
                onClick={() => setBillingCycle("monthly")}
              >
                Monthly
              </span>
              <div
                className="relative w-10 h-5 sm:w-12 sm:h-6 bg-gray-700 rounded-full cursor-pointer hover:bg-gray-600 transition-colors duration-200"
                onClick={() =>
                  setBillingCycle(
                    billingCycle === "monthly" ? "yearly" : "monthly"
                  )
                }
              >
                <motion.div
                  className="absolute w-3 h-3 sm:w-4 sm:h-4 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full top-1 left-1"
                  layout
                  transition={{
                    type: "spring",
                    stiffness: 700,
                    damping: 30,
                  }}
                  initial={{ x: billingCycle === "monthly" ? 0 : 20 }}
                  animate={{ x: billingCycle === "monthly" ? 0 : 20 }}
                />
              </div>
              <div className="relative">
                <span
                  className={`cursor-pointer text-sm font-medium transition-colors duration-200 ${
                    billingCycle === "yearly" ? "text-white" : "text-gray-400"
                  }`}
                  onClick={() => setBillingCycle("yearly")}
                >
                  Yearly
                </span>
                {billingCycle === "yearly" && (
                  <motion.span
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className="absolute -top-4 -right-2 bg-green-500 text-white text-xs px-1.5 py-0.5 rounded-full font-bold"
                  >
                    20%
                  </motion.span>
                )}
              </div>
            </div>

            {/* Plans Grid - Mobile: Vertical Stack, Desktop: Horizontal */}
            <div className="px-3 sm:px-6 pb-4 sm:pb-6">
              {/* Mobile Layout - Vertical Stack */}
              <div className="block sm:hidden space-y-3">
                {plans.map((plan, index) => (
                  <motion.div
                    key={plan.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className={`bg-gradient-to-r from-gray-800/80 to-gray-900/80 p-3 rounded-lg border backdrop-blur-sm ${
                      index === 1
                        ? "border-purple-500 ring-1 ring-purple-500/30"
                        : "border-gray-700"
                    } relative`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-1">
                          <h3 className="text-lg font-bold text-white">
                            {plan.name}
                          </h3>
                          {index === 1 && (
                            <span className="bg-purple-500 text-white text-xs px-2 py-0.5 rounded-full font-bold">
                              POPULAR
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-purple-300 mb-2">
                          {plan.flux} Flux/month
                        </p>
                        <div className="flex items-baseline space-x-1">
                          <span className="text-xl font-bold text-white">
                            {billingCycle === "monthly"
                              ? plan.price.monthly
                              : plan.price.yearly}{" "}
                            Pi
                          </span>
                          <span className="text-xs text-gray-400">
                            /{billingCycle === "monthly" ? "mo" : "yr"}
                          </span>
                        </div>
                      </div>
                      <div className="ml-4">
                        <button
                          onClick={() => handleSubscribe(plan)}
                          className={`px-4 py-2 rounded-lg text-sm font-bold transition-all duration-300 ${
                            index === 1
                              ? "bg-gradient-to-r from-purple-600 to-pink-600 text-white"
                              : "bg-gray-700 text-white hover:bg-purple-600"
                          }`}
                        >
                          Subscribe
                        </button>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Desktop Layout - Horizontal Grid */}
              <div className="hidden sm:grid grid-cols-3 gap-4 lg:gap-6">
                {plans.map((plan, index) => (
                  <motion.div
                    key={plan.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className={`bg-gradient-to-br from-gray-800/80 to-gray-900/80 p-4 lg:p-6 rounded-xl border transition-all duration-300 hover:scale-105 backdrop-blur-sm ${
                      index === 1
                        ? "border-purple-500 shadow-lg shadow-purple-500/20 ring-2 ring-purple-500/30 scale-105"
                        : "border-gray-700 hover:border-purple-400"
                    } relative`}
                  >
                    {index === 1 && (
                      <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                        <div className="bg-gradient-to-r from-purple-500 to-pink-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                          MOST POPULAR
                        </div>
                      </div>
                    )}

                    <div className="text-center">
                      <h3 className="text-xl lg:text-2xl font-bold text-white mb-2">
                        {plan.name}
                      </h3>
                      <p className="text-purple-300 text-sm mb-3">
                        {plan.flux} Flux/month
                      </p>
                      <div className="mb-4">
                        <p className="text-2xl lg:text-3xl font-bold text-white">
                          {billingCycle === "monthly"
                            ? plan.price.monthly
                            : plan.price.yearly}{" "}
                          Pi
                        </p>
                        <span className="text-sm text-gray-400">
                          per {billingCycle === "monthly" ? "month" : "year"}
                        </span>
                      </div>
                      <button
                        onClick={() => handleSubscribe(plan)}
                        className={`w-full font-bold py-2.5 rounded-lg text-sm transition-all duration-300 mb-4 ${
                          index === 1
                            ? "bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:shadow-xl hover:shadow-purple-500/30"
                            : "bg-gradient-to-r from-gray-600 to-gray-700 text-white hover:from-purple-600 hover:to-pink-600"
                        }`}
                      >
                        Get Started
                      </button>
                    </div>

                    {/* Compact advantages list */}
                    <div className="space-y-2">
                      {plan.advantages
                        .slice(0, 3)
                        .map((advantage, advIndex) => (
                          <div
                            key={advIndex}
                            className="flex items-start space-x-2"
                          >
                            <CheckCircle className="w-3 h-3 text-green-400 flex-shrink-0 mt-1" />
                            <span className="text-gray-200 text-xs leading-relaxed">
                              {advantage}
                            </span>
                          </div>
                        ))}
                      {plan.advantages.length > 3 && (
                        <div className="text-center">
                          <span className="text-purple-300 text-xs">
                            +{plan.advantages.length - 3} more features
                          </span>
                        </div>
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Footer */}
            <div className="border-t border-gray-700 p-3 sm:p-4 text-center">
              <p className="text-xs text-gray-400 mb-2">
                Cancel anytime • 30-day money-back guarantee
              </p>
              <div className="flex justify-center items-center space-x-4">
                <div className="flex items-center space-x-1">
                  <div className="w-1.5 h-1.5 bg-green-500 rounded-full"></div>
                  <span className="text-xs text-gray-400">SSL Secured</span>
                </div>
                <div className="flex items-center space-x-1">
                  <div className="w-1.5 h-1.5 bg-blue-500 rounded-full"></div>
                  <span className="text-xs text-gray-400">24/7 Support</span>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default SubscribeModal;
